import { LayerFilterOption } from "fond/map/Legend/Adornments/LayerFilter";
import { BroadbandProvider, ConfigurationUpsert, GroupConfig } from "fond/types";
import { FilterConfiguration, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";

import { FamilialLayerConfig, groupConfigTemplate, layerConfigTemplate, OverlayIds, sublayerTemplate } from "./configuration";

export enum FccLayerId {
  FCC_FIBER_FOOTPRINT = "fcc-fiber-footprint",
  FCC_MAX_DOWNLOAD = "fcc-max-download",
  FCC_ALL_FIBER_PROVIDER_COVERAGE = "fcc-all-fiber-provider-coverage",
}

export const fccLayerFilterOptions: LayerFilterOption[] = [
  { ID: "Broadband", Label: "Broadband", Type: "Category" },
  { ID: FccLayerId.FCC_FIBER_FOOTPRINT, Label: "Fiber Footprint", Type: "Option" },
  { ID: FccLayerId.FCC_MAX_DOWNLOAD, Label: "Max Download Speeds", Type: "Option" },
  { ID: FccLayerId.FCC_ALL_FIBER_PROVIDER_COVERAGE, Label: "All Fiber Provider Coverage", Type: "Option" },
];

export const defaultFccFilteredLayerIds = [FccLayerId.FCC_MAX_DOWNLOAD];

const key = "census-tracts";

const fccMaxDownloadBuckets: { id: string; label: string; color: string; filter: FilterConfiguration }[] = [
  {
    id: "fcc-max-download-0-25",
    label: "0 - 25",
    color: "#d53e4f",
    filter: {
      Mapbox: ["all", [">=", ["get", "max_download"], 0], ["<", ["get", "max_download"], 25]],
      Type: "group",
    },
  },
  {
    id: "fcc-max-download-25-100",
    label: "25 - 100",
    color: "#f96a00",
    filter: {
      Mapbox: ["all", [">=", ["get", "max_download"], 25], ["<", ["get", "max_download"], 100]],
      Type: "group",
    },
  },
  {
    id: "fcc-max-download-100-500",
    label: "100 - 500",
    color: "#f5c802",
    filter: {
      Mapbox: ["all", [">=", ["get", "max_download"], 100], ["<", ["get", "max_download"], 500]],
      Type: "group",
    },
  },
  {
    id: "fcc-max-download-500-1000",
    label: "500 - 1,000",
    color: "#c9e816",
    filter: {
      Mapbox: ["all", [">=", ["get", "max_download"], 500], ["<", ["get", "max_download"], 1000]],
      Type: "group",
    },
  },
  {
    id: "fcc-max-download-1000+",
    label: "1,000+",
    color: "#4dd53e",
    filter: {
      Mapbox: [">=", ["get", "max_download"], 1000],
      Type: "expression",
    },
  },
];

const technologies = [
  { technology: "Fiber", color: "#fb8072" },
  { technology: "Cable", color: "#bebada" },
  { technology: "Power", color: "#b3de69" },
  { technology: "Satellite", color: "#fdb462" },
  { technology: "Wireless", color: "#80b1d3" },
  { technology: "DSL", color: "#8dd3c7" },
  { technology: "Copper", color: "#ffffb3" },
];

const providerColorPalette = [
  "#e6194b",
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#42d4f4",
  "#f032e6",
  "#bfef45",
  "#fabed4",
  "#469990",
  "#dcbeff",
  "#9A6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
  "#808000",
  "#ffd8b1",
  "#000075",
  "#a9a9a9",
];

export const getProviderColor = (providerName: string): string => {
  let hash = 5381;
  for (let i = 0; i < providerName.length; i++) {
    hash = (hash * 33) ^ providerName.charCodeAt(i);
  }
  return providerColorPalette[Math.abs(hash) % providerColorPalette.length];
};

/**
 * Create a combined layer with one sublayer per fiber provider.
 */
export const createCombinedFiberProviderLayer = (fiberProviders: BroadbandProvider[]): FamilialLayerConfig => {
  const layerConfigId = FccLayerId.FCC_ALL_FIBER_PROVIDER_COVERAGE;

  const descendants: (LayerStyle | SublayerConfig)[] = fiberProviders.flatMap(({ Name: providerName, Color: color }) => {
    const id = `${layerConfigId}-${providerName}`;

    // Fiber providers can optionally have a color specified in the data; if not we (deterministically) generate
    // one based on the name.
    const finalColor = color || getProviderColor(providerName);
    const filter = {
      Mapbox: ["in", "Fiber", ["get", `broadband_provider_${providerName}`]],
      Type: "expression" as const,
    };
    const styles = maxDownloadStyles(id, finalColor, filter, 0.5);
    const subLayer = sublayerTemplate({ id, key, layerId: layerConfigId, label: providerName, filter, styles: [...styles] });
    return [...styles, subLayer];
  });

  const config = layerConfigTemplate({
    id: layerConfigId,
    key,
    label: "All Fiber Provider Coverage",
    styles: [],
    children: descendants.filter(({ Type }) => Type === "SUBLAYER").map(({ ID }) => ID),
    parentId: OverlayIds.FCC,
  });

  return { config, descendants };
};

/**
 * Create the FCC overlay configuration.
 */
export const fccOverlayConfiguration = (
  providers?: string[],
  fiberProviders?: BroadbandProvider[]
): { configs: ConfigurationUpsert; groupConfig: GroupConfig } => {
  const { config: fiberFootprintLayerConfig, descendants: fiberFootprintDescendants } = createFiberFootprintLayer();
  const { config: maxDownloadLayerConfig, descendants: maxDownloadDescendants } = createMaxDownloadLayer();

  // The 'big three' providers are always included, other layers are generated dynamically.
  const allProviders = [
    { name: "AT&T" },
    { name: "T-Mobile" },
    { name: "Verizon" },
    ...(providers ?? []).filter((name) => !["AT&T", "T-Mobile", "Verizon"].includes(name)).map((name) => ({ name })),
  ];

  const allProviderConfigs = allProviders.map(({ name }) => createProviderLayer(`${name} Coverage`, name));

  const combinedFiber = createCombinedFiberProviderLayer(fiberProviders ?? []);

  // Assemble everything together into one group.
  const layerConfigs = [fiberFootprintLayerConfig, maxDownloadLayerConfig, combinedFiber.config, ...allProviderConfigs.map(({ config }) => config)];
  const descendants = [
    ...fiberFootprintDescendants,
    ...maxDownloadDescendants,
    ...combinedFiber.descendants,
    ...allProviderConfigs.flatMap(({ descendants }) => descendants),
  ];

  // Create the groups
  const fccGroup = groupConfigTemplate(OverlayIds.FCC, layerConfigs, "National Broadband Map", true, OverlayIds.ROOT);

  // Assemble everything together
  return { configs: [...descendants, ...layerConfigs], groupConfig: fccGroup };
};

/**
 * Create the provider (e.g. AT&T, T-Mobile) layer having service type sublayers.
 */
const createProviderLayer = (label: string, provider: string): FamilialLayerConfig => {
  const layerConfigId = `fcc-${provider}-service-types`;

  const descendants: (LayerStyle | SublayerConfig)[] = technologies.flatMap(({ technology, color }) => {
    const id = `${layerConfigId}-${technology}`;
    const filter = {
      Mapbox: ["in", technology, ["get", `broadband_provider_${provider}`]],
      Type: "expression" as const,
    };
    const styles = maxDownloadStyles(id, color, filter, 0.8);
    const subLayer = sublayerTemplate({ id, key, layerId: layerConfigId, label: technology, filter, styles: [...styles] });
    return [...styles, subLayer];
  });

  const config = layerConfigTemplate({
    id: layerConfigId,
    key,
    label,
    styles: [],
    children: descendants.filter(({ Type }) => Type === "SUBLAYER").map(({ ID }) => ID),
    parentId: OverlayIds.FCC,
  });

  return { config, descendants };
};

/**
 * Create the max download speed layer.
 */
const createMaxDownloadLayer = (): FamilialLayerConfig => {
  // Use the buckets to make sublayers for the max download speed layer
  const descendants = fccMaxDownloadBuckets.flatMap((bucket) => {
    const styles = maxDownloadStyles(bucket.id, bucket.color, bucket.filter, 0.3);
    const sublayerConfig = sublayerTemplate({
      id: bucket.id,
      key,
      layerId: FccLayerId.FCC_MAX_DOWNLOAD,
      label: bucket.label,
      filter: bucket.filter,
      styles,
    });
    return [...styles, sublayerConfig];
  });

  const config = layerConfigTemplate({
    id: FccLayerId.FCC_MAX_DOWNLOAD,
    key,
    label: "Max Download Speed",
    styles: [],
    children: fccMaxDownloadBuckets.map((bucket) => bucket.id),
    parentId: OverlayIds.FCC,
  });

  return { config, descendants };
};

const maxDownloadStyles = (layerId: string, color: string, filter?: FilterConfiguration | null, opacity?: number): LayerStyle[] => [
  {
    ID: `${layerId}-polygon-fill`,
    Name: `${layerId}-polygon-fill`,
    GlobalPosition: 1,
    ConfigurationID: layerId,
    ConfigurationType: "LAYER",
    Position: 0,
    MapboxStyle: {
      type: "fill",
      ...(filter?.Mapbox ? { filter: filter.Mapbox } : {}),
      paint: {
        "fill-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, opacity ?? 0.5],
        "fill-color": ["case", ["boolean", ["feature-state", "isSelected"], false], "#FFFF00", color],
      },
    },
    RawStyles: {
      Type: "fill",
      FillOpacity: opacity ?? 0.5,
      FillColor: color,
    },
    Type: "STYLE",
  },
];

/**
 * Create the Fiber Footprint layer.
 */
const createFiberFootprintLayer = (): FamilialLayerConfig => {
  const descendants = fiberFootprintStyles(FccLayerId.FCC_FIBER_FOOTPRINT, "#fb8072");
  const config = layerConfigTemplate({
    id: FccLayerId.FCC_FIBER_FOOTPRINT,
    key,
    label: "Fiber Footprint",
    styles: [...descendants],
    children: [],
    parentId: OverlayIds.FCC,
  });

  return { config, descendants };
};

const fiberFootprintStyles = (layerId: string, color: string): LayerStyle[] => [
  {
    ID: `${layerId}-polygon-fill`,
    Name: "fcc-fiber-served-polygon-fill",
    GlobalPosition: 1,
    ConfigurationID: layerId,
    ConfigurationType: "LAYER",
    Position: 0,
    MapboxStyle: {
      type: "fill",
      filter: ["==", ["get", "fiber_served"], true],
      paint: {
        "fill-opacity": ["case", ["boolean", ["feature-state", "isEditing"], false], 0, 0.3],
        "fill-color": ["case", ["boolean", ["feature-state", "isSelected"], false], "#FFFF00", color],
      },
    },
    RawStyles: {
      Type: "fill",
      FillOpacity: 0.5,
      FillColor: color,
    },
    Type: "STYLE",
  },
];
